import round from 'lodash/round';

export function calculateBudget(share, budget) {
  return (budget || 0) * (share || 0);
}

export function calculateShare(budget, netBudget) {
  return budget / netBudget;
}

export function calculateNetBudget(netClientBudget, headerDiscount, coInvest) {
  return netClientBudget / (1 - headerDiscount) + coInvest;
}

export function calculateSpendableBudget(netBudget, adjustedFee, freeService) {
  return round(netBudget / (1 + adjustedFee) + freeService, 2);
}

export function calculateSpendableBudgetFromRawValues(
  netClientBudget,
  headerDiscount,
  coInvest,
  adjustedFee,
  freeService,
) {
  const netBudget = calculateNetBudget(
    netClientBudget,
    headerDiscount,
    coInvest,
  );
  return calculateSpendableBudget(netBudget, adjustedFee, freeService);
}

export const calculateNetBudgetFromQL = (ql, headerDiscountPercentage) => {
  const netClientBudget = ql.netClientBudgetExternal + ql.wkzBudget || 0;
  const headerDiscount = headerDiscountPercentage || 0;
  const coInvest = ql.coInvest || 0;
  return calculateNetBudget(netClientBudget, headerDiscount, coInvest);
};

export function calculateSpendableBudgetFromQL(
  quoteLine,
  headerDiscountPercent,
) {
  const netBudget = calculateNetBudgetFromQL(quoteLine, headerDiscountPercent);
  const adjustedFee = quoteLine.adjustedFee || 0;
  const freeService = quoteLine.freeService || 0;
  return calculateSpendableBudget(netBudget, adjustedFee, freeService);
}

export function calculateGrossOfferValue(quoteLine, headerDiscountPercent) {
  const originalFee = quoteLine.originalFee || 0;
  const spendableBudget = calculateSpendableBudgetFromQL(
    quoteLine,
    headerDiscountPercent,
  );
  return round(spendableBudget * (1 + originalFee), 2);
}

export function calculateNetClientBudgetExternal(quoteLines) {
  return quoteLines.reduce(
    (sum, quoteLine) => sum + quoteLine.netClientBudgetExternal,
    0,
  );
}

export function calculateWkzBudget(quoteLines) {
  return quoteLines.reduce((sum, quoteLine) => sum + quoteLine.wkzBudget, 0);
}

export function calculateSums(quoteLines, headerDiscountPercent) {
  const netClientBudgetExternal = calculateNetClientBudgetExternal(quoteLines);
  const wkzBudget = calculateWkzBudget(quoteLines);
  const netClientBudget = netClientBudgetExternal + wkzBudget;
  return quoteLines.reduce(
    (
      {
        coInvest,
        freeServiceValue,
        grossOfferValue,
        spendableBudget,
        netBudget,
      },
      quoteLine,
    ) => {
      const spendableBudgetForLine = calculateSpendableBudgetFromQL(
        quoteLine,
        headerDiscountPercent,
      );
      const grossOfferValueForLine = calculateGrossOfferValue(
        quoteLine,
        headerDiscountPercent,
      );
      const netBudgetForLine = calculateNetBudgetFromQL(
        quoteLine,
        headerDiscountPercent,
      );
      return {
        coInvest: round(coInvest + (quoteLine.coInvest || 0), 4),
        freeServiceValue: round(
          freeServiceValue + (quoteLine.freeService || 0),
          4,
        ),
        grossOfferValue: round(grossOfferValue + grossOfferValueForLine, 4),
        spendableBudget: round(spendableBudget + spendableBudgetForLine, 4),
        netBudget: round(netBudget + netBudgetForLine, 4),
        netClientBudget: round(netClientBudget, 4),
        netClientBudgetExternal: round(netClientBudgetExternal, 4),
        wkzBudget: round(wkzBudget, 4),
      };
    },
    {
      coInvest: 0,
      freeServiceValue: 0,
      grossOfferValue: 0,
      spendableBudget: 0,
      netBudget: 0,
      netClientBudget: round(netClientBudget, 4),
    },
  );
}

export function calculateInvoiceSums({ quote, quoteLinesById }) {
  const { quoteLines: quoteLineIds, headerDiscountPercent } = quote;
  const quoteLines = quoteLineIds.map(id => quoteLinesById[id]);

  const sums = calculateSums(quoteLines, headerDiscountPercent);

  sums.feeDiscount = round(
    sums.grossOfferValue - sums.freeServiceValue - sums.netBudget,
    2,
  );
  sums.headerDiscount = round(
    sums.netClientBudget / (1 - (headerDiscountPercent || 0)) -
      sums.netClientBudget,
    2,
  );
  sums.grossOfferValue = round(
    sums.netClientBudget +
      sums.freeServiceValue +
      sums.coInvest +
      sums.headerDiscount +
      sums.feeDiscount,
    2,
  );
  return sums;
}

export function calculateHeaderDiscount(
  budget,
  netClientBudget,
  coInvest,
  freeService,
  adjustedFee,
) {
  return (
    1 -
    netClientBudget / ((budget - freeService) * (1 + adjustedFee) - coInvest)
  );
}
